import { Button as AriaButton } from 'ariakit/button';
import type { ButtonHTMLAttributes, ForwardedRef } from 'react';
import { forwardRef, memo, useMemo } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';
import RIcon, { type RIconComponent } from '../remix-icon';

interface BaseLinkButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  color?: string;
  iconName?: IconName;
  icon?: RIconComponent;
  isUnderlined?: boolean;
  iconClassName?: string;
  noAriaLabel?: boolean;
  underlineOnHover?: boolean;
}

export type IInlineTextButtonProps = BaseLinkButtonProps &
  (
    | { isRightIcon?: boolean; isLeftIcon?: never }
    | { isLeftIcon?: boolean; isRightIcon?: never }
  );

export const InlineTextButton = forwardRef(
  (
    {
      label = '',
      color,
      iconName = IconName.ExternalLink,
      icon,
      isRightIcon,
      isLeftIcon,
      isUnderlined = false,
      iconClassName,
      noAriaLabel = false,
      underlineOnHover = true,
      ...rest
    }: IInlineTextButtonProps,
    ref: ForwardedRef<unknown>
  ) => {
    const theme = useTheme();
    const buttonColor = color ?? theme.palette.primary.main;

    const iconComponent = useMemo(() => {
      if (icon) {
        return (
          <RIcon
            icon={icon}
            size="sm"
            color={buttonColor}
            className={iconClassName}
          />
        );
      }

      if (iconName) {
        return (
          <StyledIcon
            name={iconName}
            scale={IconScale.S}
            color={buttonColor}
            className={iconClassName}
            ariaHidden
          />
        );
      }
      return null;
    }, [icon, iconName, buttonColor, iconClassName]);

    return (
      <StyledButton
        ref={ref}
        $color={buttonColor}
        aria-label={
          noAriaLabel
            ? undefined
            : isRightIcon && iconName === IconName.ExternalLink
              ? `${label} opens in new tab`
              : `${rest['aria-label'] || label}`
        }
        {...rest}
        data-testid={label?.replaceAll(' ', '_')}
      >
        <ContentWrapper>
          {isLeftIcon && iconComponent}
          <LabelWrapper
            $color={buttonColor}
            $isUnderlined={isUnderlined}
            $underlineOnHover={underlineOnHover}
          >
            {label}
          </LabelWrapper>
          {isRightIcon && iconComponent}
        </ContentWrapper>
      </StyledButton>
    );
  }
);

InlineTextButton.displayName = 'InlineTextButton';

export default memo(InlineTextButton);

const StyledButton: StyledComponent<
  typeof AriaButton,
  DefaultTheme,
  Record<string, unknown>
> = styled(AriaButton)(
  ({ theme, $color }: { theme: ITheme; $color: string }) => ({
    ...theme.typography.bodyMd2,
    cursor: 'pointer',
    bg: 'transparent',
    margin: 0,
    padding: 0,
    color: $color,
    background: 'none',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    ':disabled': {
      color: $color ?? theme.palette.primary.main,
      opacity: 0.4,
      textDecoration: 'none',
      cursor: 'not-allowed',
    },
  })
);

const ContentWrapper = styled.span`
  ${({ theme }) => css`
    white-space: nowrap;
    display: flex;
    align-items: center;
    line-height: 1rem;
    gap: ${theme.spacing(1)};
  `}
`;

const LabelWrapper = styled.span`
  ${({
    theme,
    $color,
    $isUnderlined,
    $underlineOnHover,
  }: {
    theme: ITheme;
    $color: string;
    $isUnderlined: boolean;
    $underlineOnHover: boolean;
  }) => css`
    transition: box-shadow 0.2s linear;
    box-shadow: ${
      $isUnderlined
        ? `0px 1px 0px 0px ${$color}`
        : `0px 1px 0px 0px transparent`
    };

    ${
      $underlineOnHover &&
      css`
        ${StyledButton}:hover & {
          box-shadow: 0px 2px 0px 0px ${$color};
        }
      `
    }


    ${StyledButton}:focus-visible & {
      box-shadow: 0px 2px 0px 0px ${$color},
      outline: 2px solid ${theme.palette.focus},
      borderRadius: ${theme.spacing(0.25)},
    }
  `}
`;

const StyledIcon = styled(Icon)`
  display: inherit;
`;
